import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import { useTranslation } from "react-i18next";
import { getLanguagePreference, saveLanguagePreference } from "@/i18n/config";
import WhatsAppButton from "@/components/WhatsAppButton";
import BackToTop from "@/components/BackToTop";
import CookieConsent from "@/components/CookieConsent";

import MetaPixelProvider from "@/components/MetaPixelProvider";
import { useVisitorPresence } from "@/hooks/useVisitorPresence";
import { useConsentScripts } from "@/hooks/useConsentScripts";
import { ConsultationDrawerProvider } from "@/context/ConsultationDrawerContext";
import GlobalConsultationDrawer from "@/components/GlobalConsultationDrawer";
import ScrollToTop from "@/components/ScrollToTop";

const VisitorTracker = () => {
  useVisitorPresence();
  useConsentScripts();
  return null;
};

// Lazy load all page components
const Index = lazy(() => import("./pages/Index"));
const NotFound = lazy(() => import("./pages/NotFound"));
const DentalImplants = lazy(() => import("./pages/treatments/DentalImplants"));
const AllOnDentalImplants = lazy(() => import("./pages/treatments/AllOnDentalImplants"));
const Veneers = lazy(() => import("./pages/treatments/Veneers"));
const ZirconiumCrowns = lazy(() => import("./pages/treatments/ZirconiumCrowns"));
const HollywoodSmile = lazy(() => import("./pages/treatments/HollywoodSmile"));
const TeethWhitening = lazy(() => import("./pages/treatments/TeethWhitening"));
const RootCanal = lazy(() => import("./pages/treatments/RootCanal"));

const DHIHairTransplant = lazy(() => import("./pages/treatments/DHIHairTransplant"));
const SapphireFUE = lazy(() => import("./pages/treatments/SapphireFUE"));
const BeardTransplant = lazy(() => import("./pages/treatments/BeardTransplant"));
const EyebrowTransplant = lazy(() => import("./pages/treatments/EyebrowTransplant"));
const Botox = lazy(() => import("./pages/treatments/Botox"));
const PRP = lazy(() => import("./pages/treatments/PRP"));

const Doctors = lazy(() => import("./pages/Doctors"));
const DoctorDetail = lazy(() => import("./pages/DoctorDetail"));

const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const TravelPlanning = lazy(() => import("./pages/TravelPlanning"));
const Install = lazy(() => import("./pages/Install"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const KVKKPolicy = lazy(() => import("./pages/KVKKPolicy"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const Admin = lazy(() => import("./pages/Admin"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const LeadDetail = lazy(() => import("./pages/LeadDetail"));
const InterpretersManagement = lazy(() => import("./pages/admin/InterpretersManagement"));
const PatientsManagement = lazy(() => import("./pages/admin/PatientsManagement"));
const PatientDetail = lazy(() => import("./pages/admin/PatientDetail"));
const GalleryManagement = lazy(() => import("./pages/admin/GalleryManagement"));
const UnifiedProfile = lazy(() => import("./pages/admin/UnifiedProfile"));
const MessageTemplatesManagement = lazy(() => import("./pages/admin/MessageTemplatesManagement"));

// Interpreter portal pages
const InterpreterDashboard = lazy(() => import("./pages/interpreter/InterpreterDashboard"));
const InterpreterPatients = lazy(() => import("./pages/interpreter/InterpreterPatients"));
const InterpreterPatientDetail = lazy(() => import("./pages/interpreter/InterpreterPatientDetail"));
const InterpreterAppointments = lazy(() => import("./pages/interpreter/InterpreterAppointments"));
const InterpreterProfile = lazy(() => import("./pages/interpreter/InterpreterProfile"));
const InterpreterTreatmentPlans = lazy(() => import("./pages/interpreter/InterpreterTreatmentPlans"));
const InterpreterTreatmentPlanDetail = lazy(() => import("./pages/interpreter/InterpreterTreatmentPlanDetail"));
const InterpreterLeads = lazy(() => import("./pages/interpreter/InterpreterLeads"));
const InterpreterLeadDetail = lazy(() => import("./pages/interpreter/InterpreterLeadDetail"));
const InterpreterPerformance = lazy(() => import("./pages/interpreter/InterpreterPerformance"));

// Landing pages for ads
const DentalImplantsLanding = lazy(() => import("./pages/landing/DentalImplantsLanding"));
const HollywoodSmileLanding = lazy(() => import("./pages/landing/HollywoodSmileLanding"));
const AllOn4Landing = lazy(() => import("./pages/landing/AllOn4Landing"));
const GeneralTrafficLanding = lazy(() => import("./pages/landing/GeneralTrafficLanding"));

const queryClient = new QueryClient();

const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
  </div>
);

/**
 * Redirect helper: /tr/* → /* (strip TR prefix for backward compat)
 */
const TRRedirect = () => {
  const location = useLocation();
  const newPath = location.pathname.replace(/^\/tr/, '') || '/';
  return <Navigate to={newPath + location.search + location.hash} replace />;
};

const LanguageRouter = () => {
  const { i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  
  useEffect(() => {
    const path = location.pathname;
    
    // Skip special routes
    if (path === '/install' || path.startsWith('/admin') || path.startsWith('/interpreter')) {
      return;
    }
    
    // Handle /:lang/interpreter → /interpreter redirect
    if (path.match(/^\/(en|de|fr)\/interpreter/)) {
      const newPath = path.replace(/^\/(en|de|fr)/, '');
      navigate(newPath, { replace: true });
      return;
    }
    
    // Check if path has a foreign language prefix (EN/DE/FR)
    const langMatch = path.match(/^\/(en|de|fr)(\/|$)/);
    
    if (langMatch && langMatch[1]) {
      const urlLang = langMatch[1];
      saveLanguagePreference(urlLang);
      if (urlLang !== i18n.language) {
        i18n.changeLanguage(urlLang);
      }
    } else {
      // No foreign prefix → Turkish (default)
      saveLanguagePreference('tr');
      if (i18n.language !== 'tr') {
        i18n.changeLanguage('tr');
      }
    }
  }, [i18n, location.pathname, navigate]);

  return (
    <MetaPixelProvider>
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          {/* === /tr/* backward compatibility redirects === */}
          <Route path="/tr" element={<TRRedirect />} />
          <Route path="/tr/*" element={<TRRedirect />} />

          {/* === Landing pages (with lang prefix) === */}
          <Route path="/:lang/landing/dental-implants" element={<DentalImplantsLanding />} />
          <Route path="/:lang/landing/hollywood-smile" element={<HollywoodSmileLanding />} />
          <Route path="/:lang/landing/all-on-4" element={<AllOn4Landing />} />
          <Route path="/:lang/landing/general" element={<GeneralTrafficLanding />} />

          {/* === Foreign language routes (EN/DE/FR with /:lang prefix) === */}
          <Route path="/:lang" element={<Index />} />
          <Route path="/:lang/treatments/dental-implants" element={<DentalImplants />} />
          <Route path="/:lang/treatments/all-on-4-dental-implants" element={<AllOnDentalImplants />} />
          <Route path="/:lang/treatments/veneers" element={<Veneers />} />
          <Route path="/:lang/treatments/zirconium-crowns" element={<ZirconiumCrowns />} />
          <Route path="/:lang/treatments/hollywood-smile" element={<HollywoodSmile />} />
          <Route path="/:lang/treatments/teeth-whitening" element={<TeethWhitening />} />
          <Route path="/:lang/treatments/root-canal" element={<RootCanal />} />
          
          <Route path="/:lang/treatments/dhi-hair-transplant" element={<DHIHairTransplant />} />
          <Route path="/:lang/treatments/sapphire-fue" element={<SapphireFUE />} />
          <Route path="/:lang/treatments/beard-transplant" element={<BeardTransplant />} />
          <Route path="/:lang/treatments/eyebrow-transplant" element={<EyebrowTransplant />} />
          <Route path="/:lang/treatments/botox" element={<Botox />} />
          <Route path="/:lang/treatments/prp" element={<PRP />} />
          
          <Route path="/:lang/doctors" element={<Doctors />} />
          <Route path="/:lang/doctors/:doctorId" element={<DoctorDetail />} />
          
          <Route path="/:lang/about" element={<About />} />
          <Route path="/:lang/contact" element={<Contact />} />
          <Route path="/:lang/travel-planning" element={<TravelPlanning />} />
          <Route path="/:lang/blog" element={<Blog />} />
          <Route path="/:lang/blog/:postId" element={<BlogPost />} />
          <Route path="/:lang/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/:lang/gizlilik-politikasi" element={<PrivacyPolicy />} />
          <Route path="/:lang/kvkk-aydinlatma-metni" element={<KVKKPolicy />} />
          <Route path="/:lang/cookie-policy" element={<CookiePolicy />} />
          <Route path="/:lang/cerez-politikasi" element={<CookiePolicy />} />
          <Route path="/:lang/terms-of-service" element={<TermsOfService />} />
          <Route path="/:lang/hizmet-sartlari" element={<TermsOfService />} />

          {/* === Turkish routes (prefix-free, default) === */}
          <Route path="/" element={<Index />} />
          <Route path="/treatments/dental-implants" element={<DentalImplants />} />
          <Route path="/treatments/all-on-4-dental-implants" element={<AllOnDentalImplants />} />
          <Route path="/treatments/veneers" element={<Veneers />} />
          <Route path="/treatments/zirconium-crowns" element={<ZirconiumCrowns />} />
          <Route path="/treatments/hollywood-smile" element={<HollywoodSmile />} />
          <Route path="/treatments/teeth-whitening" element={<TeethWhitening />} />
          <Route path="/treatments/root-canal" element={<RootCanal />} />
          
          <Route path="/treatments/dhi-hair-transplant" element={<DHIHairTransplant />} />
          <Route path="/treatments/sapphire-fue" element={<SapphireFUE />} />
          <Route path="/treatments/beard-transplant" element={<BeardTransplant />} />
          <Route path="/treatments/eyebrow-transplant" element={<EyebrowTransplant />} />
          <Route path="/treatments/botox" element={<Botox />} />
          <Route path="/treatments/prp" element={<PRP />} />
          
          <Route path="/doctors" element={<Doctors />} />
          <Route path="/doctors/:doctorId" element={<DoctorDetail />} />
          
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/travel-planning" element={<TravelPlanning />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:postId" element={<BlogPost />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/gizlilik-politikasi" element={<PrivacyPolicy />} />
          <Route path="/kvkk-aydinlatma-metni" element={<KVKKPolicy />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />
          <Route path="/cerez-politikasi" element={<CookiePolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/hizmet-sartlari" element={<TermsOfService />} />
          
          {/* === Special routes === */}
          <Route path="/install" element={<Install />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/leads/:id" element={<LeadDetail />} />
          <Route path="/admin/profile/:id" element={<UnifiedProfile />} />
          <Route path="/admin/interpreters" element={<InterpretersManagement />} />
          <Route path="/admin/patients" element={<PatientsManagement />} />
          <Route path="/admin/patients/:id" element={<PatientDetail />} />
          <Route path="/admin/gallery" element={<GalleryManagement />} />
          <Route path="/admin/templates" element={<MessageTemplatesManagement />} />
          
          {/* === Interpreter portal === */}
          <Route path="/interpreter" element={<Navigate to="/interpreter/dashboard" replace />} />
          <Route path="/interpreter/dashboard" element={<InterpreterDashboard />} />
          <Route path="/interpreter/leads" element={<InterpreterLeads />} />
          <Route path="/interpreter/leads/:id" element={<InterpreterLeadDetail />} />
          <Route path="/interpreter/patients" element={<InterpreterPatients />} />
          <Route path="/interpreter/patients/:id" element={<InterpreterPatientDetail />} />
          <Route path="/interpreter/appointments" element={<InterpreterAppointments />} />
          <Route path="/interpreter/treatment-plans" element={<InterpreterTreatmentPlans />} />
          <Route path="/interpreter/treatment-plans/:id" element={<InterpreterTreatmentPlanDetail />} />
          <Route path="/interpreter/profile" element={<InterpreterProfile />} />
          <Route path="/interpreter/performance" element={<InterpreterPerformance />} />
          
          {/* Catch-all */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </MetaPixelProvider>
  );
};

const GlobalUIElements = () => {
  const location = useLocation();
  const isAdminOrInterpreter = location.pathname.startsWith('/admin') || location.pathname.startsWith('/interpreter');
  
  if (isAdminOrInterpreter) {
    return null;
  }
  
  return (
    <>
      <WhatsAppButton />
      <BackToTop />
      
      <CookieConsent />
    </>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <ConsultationDrawerProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScrollToTop />
          <VisitorTracker />
          <LanguageRouter />
          <GlobalUIElements />
          <GlobalConsultationDrawer />
        </BrowserRouter>
      </ConsultationDrawerProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
